import { Button, Card, Stack, Link, Typography } from '@mui/material'
import { EntityImagePreviewModal } from '../../common/EntityImagePreviewModal'
import { ProjectCoverImage } from '../../common/ProjectCoverImage'
import { useEntityImagePreview } from '../../common/useEntityImagePreview'
import { CardInfo, BoxImg, LikeJoinStack, TypTags, linkStyle } from './style'
import { IconProjectLink } from '../../../icons/IconProjectLink'
import modalEmitter from '../../ModalComponent/modalEmitter'
import { JoinProjectModalBody, openLeaveProjectModal, ProjectMembersModalBody, openPlatformReportModal } from '../../Modals'
import { ProjectExternalLinksSection } from '../ProjectExternalLinksSection'
import { routesProfile } from '../../../consts'
import { useNavigate } from 'react-router-dom'
import { MouseEvent, useEffect, useRef, useState } from 'react'
import { IProjectResponse } from '../../../api/types'
import { profileApi } from '../../../api/profile'
import { formatNumber } from '../../../common/FormatNumber'
import { formatApiDateToDdMmYyyy, websiteHref } from '../../../utils/common'
import { useLikeProjectMutation, useSubscribeProjectMutation, useUnlikeProjectMutation, useUnsubscribeProjectMutation } from '../../../api/projects'
import { toast } from 'react-toastify'
import { ProjectTypeEnum } from '../../../consts/projectTypeOptions'
import { ProjectTeamSection } from '../ProjectTeamSection'
import { ParticipatingTeamsSection } from '../ParticipatingTeamsSection'
import { SourceSocialOrderSection } from '../SourceSocialOrderSection'
import { ProjectLikeControl } from '../ProjectLikeControl'
import { ProjectTagButton } from '../ProjectTagButton'
import { useSettledToggleNetwork } from '../../../hooks/useSettledToggleNetwork'

interface ProjectInfoProps {
  project: IProjectResponse
}

export const ProjectInfo = ({project}: ProjectInfoProps) => {
  const navigation = useNavigate();
  const { data: me } = profileApi.useGetProfileQuery()
  const {
    canPreview: canPreviewCover,
    openPreview: openCoverPreview,
    closePreview: closeCoverPreview,
    galleryOpen: coverGalleryOpen,
    galleryImages: coverGalleryImages,
  } = useEntityImagePreview(project.image)
  const permissions = project.viewer?.permissions
  const canEdit = permissions?.can_edit_content || permissions?.can_moderate
  const canViewTeamSpace = permissions?.can_view_team_space === true
  const isAuthor = me?.id !== undefined && me?.id === project.author.id;
  const [subscribeProject, { isLoading: isSubscribeLoading }] = useSubscribeProjectMutation()
  const [unsubscribeProject, { isLoading: isUnsubscribeLoading }] = useUnsubscribeProjectMutation()
  const [likeProject, likeState] = useLikeProjectMutation()
  const [unlikeProject, unlikeState] = useUnlikeProjectMutation()
  const [isFollowed, setIsFollowed] = useState(project.is_followed === true)
  const [isLiked, setIsLiked] = useState(project.viewer?.is_liked === true)
  const [likesCount, setLikesCount] = useState(Number(project.counters.likes ?? 0))
  const [subscribersCount, setSubscribersCount] = useState(Number(project.counters?.subscribers ?? 0))
  const subscribeSettle = useSettledToggleNetwork()
  const likeSettle = useSettledToggleNetwork()
  const subscribeBurstRef = useRef<boolean | null>(null)
  const likeBurstRef = useRef<{ value: boolean; count: number } | null>(null)

  useEffect(() => {
    setIsFollowed(project.is_followed === true)
    setSubscribersCount(Number(project.counters?.subscribers ?? 0))
  }, [project.id, project.is_followed, project.counters?.subscribers])

  useEffect(() => {
    setIsLiked(project.viewer?.is_liked === true)
    setLikesCount(Number(project.counters.likes ?? 0))
  }, [project.id, project.viewer?.is_liked, project.counters?.likes])

  const handleEdit = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    navigation(`/${routesProfile.PROJECTS.path}/${project.id}/edit`)
  };
  const handleJoinProject = () => {
    modalEmitter.emitOpenModal({
      dialog: {
        title: "Присоединиться к проекту",
        body: <JoinProjectModalBody projectId={project.id} />,
      },
      closeAtion: { visible: false },
      confirmAtion: { visible: false },
    })
  }

  const handleLeaveProject = () => {
    openLeaveProjectModal(project.id)
  }

  const handleToggleSubscribe = () => {
    if (isSubscribeLoading || isUnsubscribeLoading) return
    if (subscribeBurstRef.current === null) {
      subscribeBurstRef.current = isFollowed
    }
    const next = !isFollowed
    setIsFollowed(next)
    setSubscribersCount((n) => Math.max(0, n + (isFollowed ? -1 : 1)))

    subscribeSettle.schedule({
      desired: next,
      commit: async (desired) => {
        subscribeBurstRef.current = null
        if (desired) {
          await subscribeProject({ projectId: project.id, slug: project.slug }).unwrap()
          toast.success("Вы подписались на проект")
        } else {
          await unsubscribeProject({ projectId: project.id, slug: project.slug }).unwrap()
          toast.success("Вы отписались от проекта")
        }
      },
      onSkip: () => {
        subscribeBurstRef.current = null
      },
      onError: (baseline) => {
        subscribeBurstRef.current = null
        setIsFollowed(baseline)
        setSubscribersCount(Number(project.counters?.subscribers ?? 0))
        toast.error("Не удалось изменить подписку")
      },
    })
  }

  const handleToggleLike = () => {
    if (likeState.isLoading || unlikeState.isLoading) return
    if (!me?.id || !project.viewer) {
      toast.info("Войдите, чтобы ставить лайки")
      return
    }
    if (!likeBurstRef.current) {
      likeBurstRef.current = { value: isLiked, count: likesCount }
    }
    const next = !isLiked
    setIsLiked(next)
    setLikesCount((n) => Math.max(0, n + (isLiked ? -1 : 1)))

    likeSettle.schedule({
      desired: next,
      commit: async (desired) => {
        likeBurstRef.current = null
        if (desired) {
          await likeProject({ projectId: project.id, slug: project.slug }).unwrap()
        } else {
          await unlikeProject({ projectId: project.id, slug: project.slug }).unwrap()
        }
      },
      onSkip: () => {
        likeBurstRef.current = null
      },
      onError: () => {
        const burst = likeBurstRef.current
        likeBurstRef.current = null
        if (burst) {
          setIsLiked(burst.value)
          setLikesCount(burst.count)
        }
        toast.error('Не удалось изменить лайк')
      },
    })
  }

  const viewsCount = Number(project.counters?.views ?? 0)
  const membersCount = Number(project.counters?.members ?? 0)
  const teamPreview = (project.team_preview ?? []).slice(0, 5)
  const participatingTeams = project.participating_teams ?? []
  const openMembersModal = () => {
    modalEmitter.emitOpenModal({
      dialog: {
        title: 'Вся команда',
        body: <ProjectMembersModalBody projectId={project.id} />,
        modalToCentrPosition: true,
      },
    })
  }

  const externalLinks = project.external_links ?? []

  return (
    <Card variant="primaryCard" sx={CardInfo}>
      <Stack direction="column" gap={1} mb={4}>
        <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between">
          <Typography variant="black-16-semiBold" component="div">{project.type === ProjectTypeEnum.ONE_TIME ? "Единоразовый проект" : "Цикличный проект"}</Typography>
          {canEdit ? (
            <Button variant="follow" onClick={handleEdit}>Редактировать</Button>
          ) : (
            <Button
              variant="follow"
              onClick={handleToggleSubscribe}
              disabled={isSubscribeLoading || isUnsubscribeLoading}
            >
              {isFollowed ? "Отписаться" : "Подписаться"}
            </Button>
          )}
        </Stack>
        <ProjectCoverImage
          src={project.image}
          alt="Image"
          sx={BoxImg}
          onClick={canPreviewCover ? openCoverPreview : undefined}
        />
        <Stack direction="row" gap={1} alignItems="center" sx={LikeJoinStack}>
          <ProjectLikeControl
            projectId={project.id}
            isLiked={isLiked}
            likesCount={likesCount}
            likesPreview={project.likes_preview}
            onToggleLike={handleToggleLike}
            loading={likeState.isLoading || unlikeState.isLoading}
          />
          {!isAuthor && (
            project.is_joined ? (
              <Button
                variant="danger"
                onClick={handleLeaveProject}
                sx={{ flex: 1, maxHeight: 32 }}
              >
                Выбыть
              </Button>
            ) : (
              <Button variant="action" onClick={handleJoinProject} sx={{ flex: 1, maxHeight: 32 }}>
                Присоединиться
              </Button>
            )
          )}
        </Stack>
        {canViewTeamSpace ? (
          <Button
            variant="follow"
            onClick={() => navigation(`/${routesProfile.PROJECTS.path}/${project.slug}/team-space`)}
            sx={{ alignSelf: 'stretch', maxHeight: 32 }}
          >
            Командное пространство
          </Button>
        ) : null}
        {!isAuthor ? (
          <Button
            variant="text"
            size="small"
            onClick={() =>
              openPlatformReportModal({
                targetType: 'project',
                targetId: project.id,
                dialogTitle: 'Пожаловаться на проект',
              })
            }
            sx={{ alignSelf: 'flex-start', px: 0 }}
          >
            Пожаловаться на проект
          </Button>
        ) : null}
      </Stack>
      {(project.website || externalLinks.length > 0) && (
        <Stack gap={1} mb={4}>
          {project.website && (
            <>
              <Typography variant='black-14-medium' sx={{ lineHeight: '18px' }}>Сайт проекта</Typography>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <IconProjectLink />
                <Link
                    component="a"
                    href={websiteHref(project.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="black-14-light"
                    noWrap
                    sx={linkStyle}
                >
                    {project.website}
                </Link>
              </Stack>
            </>
          )}
          <ProjectExternalLinksSection links={externalLinks} />
        </Stack>
      )}

      <Stack direction="column" gap={2}>
        <SourceSocialOrderSection sourceSocialOrder={project.source_social_order} />
        <ProjectTeamSection
          members={teamPreview}
          membersTotal={membersCount}
          onOpenAllMembers={openMembersModal}
        />
        <ParticipatingTeamsSection teams={participatingTeams} />
        <Stack gap={1}>
          <Typography variant='black-14-medium' sx={{ lineHeight: '18px' }}>Подписчиков: {formatNumber(subscribersCount)}</Typography>
          <Typography variant='black-14-medium' sx={{ lineHeight: '18px' }}>Просмотров: {formatNumber(viewsCount)}</Typography>
        </Stack>
        <Stack gap={1}>
          <Typography variant="black-14-light" sx={{ lineHeight: "18px" }}>Создан: {formatApiDateToDdMmYyyy(project.created_at)}</Typography>
          <Typography variant="black-14-light" sx={{ lineHeight: "18px" }}>Отредактирован: {formatApiDateToDdMmYyyy(project.updated_at)}</Typography>
          <Typography variant="black-14-light" sx={{ lineHeight: "18px", overflowWrap: 'anywhere', }}>Местоположение: {project.region}</Typography>
        </Stack>
      </Stack>

      {project.tags != null && project.tags.length > 0 && (
        <>
          <Typography variant="black-14-medium" component="div" mt={4} mb={3} sx={{ lineHeight: '18px',  }}>Направления:</Typography>
          <Stack direction="row" gap={1.5} flexWrap="wrap">
            {project.tags.map((tag, index) => (
              <ProjectTagButton
                key={`${tag}-${index}`}
                tag={tag}
                sx={TypTags}
              />
            ))}
          </Stack>
        </>
      )}
      <EntityImagePreviewModal
        open={coverGalleryOpen}
        onClose={closeCoverPreview}
        images={coverGalleryImages}
      />
    </Card>
  )
}
